import React, {useEffect} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {
    getBottomPerformers,
    getCapitalByProduct,
    getCapitalByStrategy,
    getCapitalDeploymentSummary,
    getTopPerformers,
} from '../api/metrics';
import './CapitalDeployment.css';
import './shared/MetricCards.css';
import DashboardBottomLogo from './DashboardBottomLogo';
import SectionTitle from './shared/SectionTitle';
import StandaloneSection from './shared/StandaloneSection';
import CapitalAllocationChart from './metrics/CapitalAllocationChart';
import {AlertCircleIcon} from './shared/Icons';
import {useAutoRefreshTrigger, useGlobalAutoRefreshStore} from '../stores/useGlobalAutoRefreshStore';

const CapitalDeployment: React.FC = () => {
  const queryClient = useQueryClient();
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('capital');


  // Set this dashboard as active when mounted, clear when unmounted
  useEffect(() => {
    setActiveDashboard('capital');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  // When global refresh trigger changes, invalidate all queries to refetch data
  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries();
    }
  }, [refreshTrigger, queryClient]);


  // Fetch all capital deployment data
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['capitalDeploymentSummary'],
    queryFn: async () => {
        return await getCapitalDeploymentSummary();
    },
  });

  const { data: byStrategy, isLoading: strategyLoading } = useQuery({
    queryKey: ['capitalByStrategy'],
    queryFn: async () => {
        return await getCapitalByStrategy();
    },
  });

  const { data: byProduct, isLoading: productLoading } = useQuery({
    queryKey: ['capitalByProduct'],
    queryFn: async () => {
        return await getCapitalByProduct();
    },
  });

  const { data: topPerformers, isLoading: topLoading } = useQuery({
    queryKey: ['topPerformers'],
    queryFn: async () => {
        return await getTopPerformers(5);
    },
  });

  const { data: bottomPerformers, isLoading: bottomLoading } = useQuery({
    queryKey: ['bottomPerformers'],
    queryFn: async () => {
        return await getBottomPerformers(5);
    },
  });

  // Format currency
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return '0.00%';
    return `${value.toFixed(decimals)}%`;
  };

  // Get performance class
  const getPerformanceClass = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value >= 0 ? 'positive' : 'negative';
  };

  if (summaryError) {
    return (
      <div className="capital-deployment">
        <div className="page-header">
          <SectionTitle
            title="Capital Deployment"
            subtitle="Monitor and optimize your capital allocation across trading strategies"
          />
        </div>
        <div className="error-message">
          <p><AlertCircleIcon size={20} /> Error loading capital deployment data</p>
          <p className="error-details">{(summaryError as Error).message}</p>
        </div>
        <DashboardBottomLogo />
      </div>
    );
  }

  return (
    <div className="capital-deployment">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <SectionTitle
          title="Capital Deployment"
        />
      </div>

      <div className="dashboard-content">

          <StandaloneSection title="ACTIVE CAPITAL OVERVIEW">
            {summaryLoading ? (
              <div className="section-loading">
                <div className="loading-spinner"></div>
                <p>Loading portfolio overview...</p>
              </div>
            ) : summary ? (
              <div className="hero-metrics">
                <div className="hero-metric hero-metric-primary">
                  <div className="hero-label">Total Allocated</div>
                  <div className="hero-value-primary">{formatCurrency(summary.totalAllocated)}</div>
                  <div className="hero-change">
                    <div className="detail-row">
                      <span>Deployed: {formatCurrency(summary.totalDeployed)}</span>
                    </div>
                    <div className="detail-row">
                      <span>Pending: {formatCurrency(summary.pendingBuyCapital)}</span>
                    </div>
                    <div className="detail-row">
                      <span>Active: {formatCurrency(summary.activePositionCapital)}</span>
                    </div>
                  </div>
                </div>

                <div className="hero-metric">
                  <div className="hero-label">Realized Profit</div>
                  <div className={`hero-value ${getPerformanceClass(summary.totalRealizedProfit)}`}>
                    {formatCurrency(summary.totalRealizedProfit)}
                  </div>
                  <div className="hero-change">From {summary.totalTrades} trades</div>
                </div>

                <div className="hero-metric">
                  <div className="hero-label">Current Profit</div>
                  <div className={`hero-value ${getPerformanceClass(summary.totalCurrentProfit)}`}>
                    {formatCurrency(summary.totalCurrentProfit)}
                  </div>
                  <div className="hero-change">Target: {formatCurrency(summary.totalTargetProfit)}</div>
                </div>

                <div className="hero-metric">
                  <div className="hero-label">Portfolio ROI</div>
                  <div className={`hero-value ${getPerformanceClass(summary.portfolioRoi)}`}>
                    {formatPercent(summary.portfolioRoi * 100, 2)}
                  </div>
                  <div className="hero-change">
                    Efficiency: {formatPercent(summary.deploymentEfficiencyPct, 1)}
                  </div>
                </div>
              </div>
            ) : null}
          </StandaloneSection>


        {/* Capital Allocation Pie Charts */}
          <CapitalAllocationChart />

        {/* Strategy Allocation */}

          <StandaloneSection title="ALLOCATION BY STRATEGY">
            {strategyLoading ? (
              <div className="section-loading">
                <div className="loading-spinner"></div>
                <p>Loading strategy data...</p>
              </div>
            ) : byStrategy && byStrategy.strategies.length > 0 ? (
              <div className="allocation-grid">
                {byStrategy.strategies.map((strategy) => (
                  <div key={strategy.strategyType} className="allocation-card">
                    <div className="allocation-header">
                      <h3>{strategy.strategyType.replace(/_/g, ' ')}</h3>
                      <span className="allocation-percent">{formatPercent(strategy.allocationPercent, 1)}</span>
                    </div>
                    <div className="allocation-bar">
                      <div
                        className="allocation-fill"
                        style={{ width: `${Math.min(strategy.allocationPercent, 100)}%` }}
                      />
                    </div>
                    <div className="allocation-stats">
                      <div className="stat-row">
                        <span>Deployed Capital:</span>
                        <span className="stat-value">{formatCurrency(strategy.deployedCapital)}</span>
                      </div>
                      <div className="stat-row">
                        <span>Realized Profit:</span>
                        <span className={`stat-value ${getPerformanceClass(strategy.realizedProfit)}`}>
                          {formatCurrency(strategy.realizedProfit)}
                        </span>
                      </div>
                      <div className="stat-row">
                        <span>ROI:</span>
                        <span className={`stat-value ${getPerformanceClass(strategy.roi)}`}>
                          {formatPercent(strategy.roi * 100, 2)}
                        </span>
                      </div>
                      <div className="stat-row">
                        <span>Active Groups:</span>
                        <span className="stat-value">{strategy.activeGroups}</span>
                      </div>
                      <div className="stat-row">
                        <span>Total Trades:</span>
                        <span className="stat-value">{strategy.totalTrades}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">No strategy data available</div>
            )}
          </StandaloneSection>


        {/* Product Allocation */}
          <StandaloneSection title="ALLOCATION BY PRODUCT">
            {productLoading ? (
              <div className="section-loading">
                <div className="loading-spinner"></div>
                <p>Loading product data...</p>
              </div>
            ) : byProduct && byProduct.products.length > 0 ? (
              <div className="product-table">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Allocation</th>
                      <th>Deployed Capital</th>
                      <th>Realized Profit</th>
                      <th>Current Price</th>
                      <th>ROI</th>
                      <th>Groups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byProduct.products.map((product) => (
                      <tr key={product.productId}>
                        <td className="product-name">{product.productId}</td>
                        <td>
                          <div className="inline-bar-container">
                            <div className="inline-bar">
                              <div
                                className="inline-bar-fill"
                                style={{ width: `${Math.min(product.allocationPercent, 100)}%` }}
                              />
                            </div>
                            <span className="inline-percent">{formatPercent(product.allocationPercent, 1)}</span>
                          </div>
                        </td>
                        <td>{formatCurrency(product.deployedCapital)}</td>
                        <td className={getPerformanceClass(product.realizedProfit)}>
                          {formatCurrency(product.realizedProfit)}
                        </td>
                        <td>{formatCurrency(product.currentPrice)}</td>
                        <td className={getPerformanceClass(product.roi)}>
                          {formatPercent(product.roi * 100, 2)}
                        </td>
                        <td>{product.activeGroups}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-data">No product data available</div>
            )}
          </StandaloneSection>

        {/* Top and Bottom Performers */}
          <div className="performers-grid">
            {/* Top Performers */}
            <StandaloneSection title="TOP PERFORMERS">
              {topLoading ? (
                <div className="section-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading top performers...</p>
                </div>
              ) : topPerformers && topPerformers.performers.length > 0 ? (
                <div className="performers-list">
                  {topPerformers.performers.map((performer, index) => (
                    <div key={performer.tradeGroupId} className="performer-item">
                      <div className="performer-rank">{index + 1}</div>
                      <div className="performer-info">
                        <div className="performer-name">{performer.name}</div>
                        <div className="performer-meta">
                          {performer.productId} • {performer.tradeGroupType.replace(/_/g, ' ')}
                        </div>
                      </div>
                      <div className="performer-stats">
                        <div className="performer-profit positive">
                          {formatCurrency(performer.totalCapturedProfit)}
                        </div>
                        <div className="performer-roi positive">
                          ROI: {formatPercent(performer.roi * 100, 2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data">No top performers data</div>
              )}
            </StandaloneSection>

            {/* Bottom Performers */}
            <StandaloneSection title="BOTTOM PERFORMERS">
              {bottomLoading ? (
                <div className="section-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading bottom performers...</p>
                </div>
              ) : bottomPerformers && bottomPerformers.performers.length > 0 ? (
                <div className="performers-list">
                  {bottomPerformers.performers.map((performer, index) => (
                    <div key={performer.tradeGroupId} className="performer-item">
                      <div className="performer-rank">{index + 1}</div>
                      <div className="performer-info">
                        <div className="performer-name">{performer.name}</div>
                        <div className="performer-meta">
                          {performer.productId} • {performer.tradeGroupType.replace(/_/g, ' ')}
                        </div>
                      </div>
                      <div className="performer-stats">
                        <div className={`performer-profit ${getPerformanceClass(performer.totalCapturedProfit)}`}>
                          {formatCurrency(performer.totalCapturedProfit)}
                        </div>
                        <div className={`performer-roi ${getPerformanceClass(performer.roi)}`}>
                          ROI: {formatPercent(performer.roi * 100, 2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data">No bottom performers data</div>
              )}
            </StandaloneSection>
          </div>

        <DashboardBottomLogo />
      </div>
    </div>
  );
};

export default CapitalDeployment;
